import React, { useEffect, useMemo, useState } from 'react';
import MUIDialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { CloseOutlined } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useDataProvider } from 'react-admin';
import { loadStripe } from '@stripe/stripe-js';
import Lottie from 'react-lottie';

import { KYCStatus, KycVerificationSessionResponse } from 'services/types/kycStatus';
import useKycStatus from 'hooks/useKycStatus';
import useProfile from 'hooks/useProfile';
import useCustomNotify from 'hooks/useCustomNotify';
import {
  closeIdentityVerificationDialog,
  setIdentityVerificationInProgress,
} from 'store/actions/identityVerificationActions';
import IdentityVerificationStatusDialog from './IdentityVerificationStatusDialog';
import VerifyIdentityAnimation from 'assets/rewards/animation/VerifyIdentity.json';
import inProgressIcon from 'assets/general/icons/inProgress.svg';
import stripeIcon from 'assets/stripeVerification/stripeIcon.svg';
import timerIcon from 'assets/stripeVerification/timerIcon.svg';
import {
  clearIdentityVerificationAutoOpenDismissed,
  getIdentityVerificationAutoOpenDismissedKey,
  getIdentityVerificationStatusKey,
  writeIdentityVerificationAutoOpenDismissed,
} from 'services/helpers/identityVerification';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    backgroundColor: theme.palette.common.white,
    width: '100%',
    maxWidth: 545,
  },
  content: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
  },
  title: {
    fontWeight: 700,
    fontSize: 24,
    lineHeight: '30px',
    color: theme.palette.text.primary,
  },
  titleLine: {
    display: 'block',
  },
  closeButton: {
    color: theme.palette.text.secondary,
  },
  heroPanel: {
    backgroundColor: '#FAFAFC',
    borderRadius: 16,
    padding: theme.spacing(4),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  heroImage: {
    width: 140,
    height: 140,
  },
  heroImageProfile: {
    width: 120,
    height: 120,
  },
  descriptionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  description: {
    color: theme.palette.text.primary,
    lineHeight: '24px',
    fontWeight: 400,
    fontSize: 16,
  },
  emphasis: {
    fontSize: 16,
    lineHeight: '20px',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  timeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '20px',
    color: theme.palette.text.primary,
  },
  timeIcon: {
    width: 20,
    height: 20,
    flexShrink: 0,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    height: 24,
    padding: '2px 8px',
    borderRadius: 8,
    backgroundColor: '#FEDACD',
    boxSizing: 'border-box',
  },
  badgeIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  badgeText: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  actionsContainer: {
    display: 'flex',
    textTransform: 'none',
  },
  actionButtonPrimary: {
    textTransform: 'none',
    borderRadius: 999,
    height: 52,
    fontWeight: 700,
  },
  actionButtonContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: '18px',
  },
  stripeIcon: {
    width: 36,
    height: 14,
  },
}));
const padTime = (value: number) => String(value).padStart(2, '0');

const formatTimeLeft = (expiresAt: number | null) => {
  if (!expiresAt) return '--:--';

  const diff = expiresAt - Date.now();

  if (diff <= 0) {
    return '00:00:00';
  }

  const totalSeconds = Math.floor(diff / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${padTime(hours)}:${padTime(minutes)}:${padTime(seconds)}`;
};

const IdentityVerificationDialog = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const dataProvider = useDataProvider();
  const notify = useCustomNotify();
  const { profile, getProfile } = useProfile();

  const { open, onCloseCallback, forceOpen, verificationInProgress, variant } = useSelector(
    (state: any) => state.identityVerification || {}
  );

  const stripeVerificationStatus = profile?.stripeVerificationStatus as string | undefined;
  const isVerifying = Boolean(verificationInProgress);
  const { status, secondsLeft, hasCountdown, isVerificationRequired } = useKycStatus(
    stripeVerificationStatus,
    isVerifying || !open
  );
  // Scope auto-open to the active login session; KYC-not-required states reset the marker.
  const loginKey = useMemo(() => {
    if (!profile?.sub) return null;

    const authTime = (profile as { auth_time?: number; iat?: number }).auth_time;
    const issuedAt = (profile as { iat?: number }).iat;
    const loginStamp = authTime ?? issuedAt ?? 'na';

    return `${profile.sub}-${loginStamp}`;
  }, [profile]);
  const autoOpenDismissedKey = useMemo(
    () => getIdentityVerificationAutoOpenDismissedKey(loginKey),
    [loginKey]
  );
  // Assumption: only the Profile entry point uses the softer "profile" copy.
  const dialogVariant = variant === 'profile' ? 'profile' : 'required';
  const isProfileVariant = dialogVariant === 'profile';
  const heroAnimationOptions = useMemo(
    () => ({
      loop: true,
      autoplay: true,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid meet',
      },
    }),
    []
  );

  const statusKey = useMemo(() => {
    return getIdentityVerificationStatusKey(status, secondsLeft, stripeVerificationStatus);
  }, [secondsLeft, status, stripeVerificationStatus]);

  const expiresAt = useMemo(() => {
    if (secondsLeft === undefined || secondsLeft === null) return null;

    const parsed = Number(secondsLeft);

    return Number.isNaN(parsed) ? null : Date.now() + parsed * 1000;
  }, [secondsLeft]);

  const [timeLeft, setTimeLeft] = useState<string>(formatTimeLeft(expiresAt));
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusDialogOpenedFromRequired, setStatusDialogOpenedFromRequired] = useState(false);

  // STARTED state should show the "reviewing" message instead of the standard content.
  const isKycStarted = status === KYCStatus.STARTED;

  useEffect(() => {
    if (isVerificationRequired) return;

    clearIdentityVerificationAutoOpenDismissed(autoOpenDismissedKey);
  }, [autoOpenDismissedKey, isVerificationRequired]);

  useEffect(() => {
    if (open && !forceOpen && !isVerifying && !isVerificationRequired) {
      dispatch(closeIdentityVerificationDialog(statusKey));
    }
  }, [dispatch, forceOpen, isVerifying, isVerificationRequired, open, statusKey]);

  useEffect(() => {
    if (!open) return;

    const updateTimeLeft = () => {
      setTimeLeft(formatTimeLeft(expiresAt));
    };

    updateTimeLeft();

    if (!expiresAt) return;

    const intervalId = setInterval(updateTimeLeft, 1000);

    return () => clearInterval(intervalId);
  }, [expiresAt, open]);

  const closeDialogState = () => {
    if (onCloseCallback) {
      onCloseCallback();
    }

    dispatch(closeIdentityVerificationDialog(statusKey));
  };

  const handleClose = (
    _event?: React.SyntheticEvent,
    reason?: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (isVerifying && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }

    if (dialogVariant === 'required') {
      writeIdentityVerificationAutoOpenDismissed(autoOpenDismissedKey);
    }

    closeDialogState();
  };

  const handleCloseStatusDialog = () => {
    setStatusDialogOpen(false);

    if (statusDialogOpenedFromRequired) {
      writeIdentityVerificationAutoOpenDismissed(autoOpenDismissedKey);
    }

    setStatusDialogOpenedFromRequired(false);
    closeDialogState();
  };

  const refreshProfile = () => {
    (getProfile as (params?: { bypassCache?: boolean; showLoading?: boolean }) => void)({
      bypassCache: true,
      showLoading: false,
    });
  };

  const handleVerifyNow = async () => {
    if (isVerifying) return;

    dispatch(setIdentityVerificationInProgress(true));

    try {
      const { data } = await dataProvider.post('kycVerificationSession', { data: {} });
      const session = data as KycVerificationSessionResponse | undefined;

      if (!session?.publishableKey || !session?.clientSecret) {
        notify('Unable to start identity verification. Please try again.', 'error');
        return;
      }

      const stripe = await loadStripe(session.publishableKey);

      if (!stripe) {
        notify('Failed to load Stripe. Please try again.', 'error');
        return;
      }

      const { error } = await stripe.verifyIdentity(session.clientSecret);

      if (error) {
        const errorMessages: Record<string, string> = {
          session_cancelled: 'Identity verification canceled.',
          consent_declined:
            'You declined to verify your account. Please resume verification to proceed.',
          device_unsupported:
            'Camera access is required for verification. Please use a compatible device.',
          under_supported_age: 'You must be of legal age to complete verification.',
          phone_otp_declined: 'Phone number verification failed. Please try again.',
          email_verification_declined: 'Email verification failed. Please try again.',
        };

        const message = errorMessages[error.code as keyof typeof errorMessages];

        notify(message || 'Identity verification finished with an error.', 'error');
        return;
      }

      if (onCloseCallback) {
        onCloseCallback();
      }

      setStatusDialogOpenedFromRequired(dialogVariant === 'required');
      dispatch(closeIdentityVerificationDialog(statusKey));
      setStatusDialogOpen(true);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unable to start identity verification.';
      notify(errorMessage, 'error');
    } finally {
      dispatch(setIdentityVerificationInProgress(false));
    }
  };

  if ((!open || (!forceOpen && !isVerificationRequired)) && !statusDialogOpen) {
    return null;
  }

  return (
    <>
      {open && (
        <MUIDialog
          open={open}
          onClose={handleClose}
          maxWidth="lg"
          fullWidth
          disableEnforceFocus={isVerifying}
          disableAutoFocus={isVerifying}
          disableRestoreFocus={isVerifying}
          classes={{ paper: classes.dialogPaper }}>
          <DialogContent className={classes.content}>
            <div className={classes.header}>
              <div className={classes.headerContent}>
                <Typography variant="h6" className={classes.title}>
                  {isProfileVariant || isKycStarted ? (
                    'Identity Verification'
                  ) : (
                    <>
                      Immediate Action Required:
                      <span className={classes.titleLine}>Identity Verification</span>
                    </>
                  )}
                </Typography>
                {isKycStarted && (
                  <div className={classes.badge}>
                    <img src={inProgressIcon} alt="" className={classes.badgeIcon} />
                    <Typography variant="body2" className={classes.badgeText}>
                      In progress
                    </Typography>
                  </div>
                )}
              </div>
              <IconButton
                aria-label="Close dialog"
                onClick={handleClose}
                className={classes.closeButton}>
                <CloseOutlined />
              </IconButton>
            </div>

            <div className={classes.heroPanel}>
              <div
                className={`${classes.heroImage} ${
                  isProfileVariant ? classes.heroImageProfile : ''
                }`}>
                <Lottie
                  options={{ ...heroAnimationOptions, animationData: VerifyIdentityAnimation }}
                  ariaRole="img"
                  ariaLabel="Identity verification required"
                  isClickToPauseDisabled
                />
              </div>
            </div>

            <div className={classes.descriptionContainer}>
              {isKycStarted ? (
                <Typography variant="body1" className={classes.description}>
                  We&apos;re reviewing your information. This usually takes a few minutes, but in
                  some cases it may take longer.
                </Typography>
              ) : isProfileVariant ? (
                <>
                  <Typography variant="body1" className={classes.description}>
                    Verify your identity to unlock additional features and increase your account
                    security.
                  </Typography>
                  <Typography variant="body1" className={classes.description}>
                    Identity verification helps us keep the platform safe and gives you access to
                    extended account capabilities, including smoother shipments and fewer
                    restrictions.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body1" className={classes.description}>
                    Your account has been flagged for mandatory identity verification.
                  </Typography>
                  <Typography variant="body1" className={classes.description}>
                    You must complete identity verification now. If you do not verify within the
                    remaining time, all shipments associated with your account will be intercepted
                    per Rollo&apos;s Terms of Service.
                  </Typography>
                  <Typography variant="body2" className={classes.description}>
                    Intercepted shipments are{' '}
                    <span className={classes.emphasis}>permanently unrecoverable.</span>
                  </Typography>
                </>
              )}
            </div>
            {!isProfileVariant && !isKycStarted && hasCountdown && (
              <div className={classes.timeRow}>
                <img src={timerIcon} alt="" className={classes.timeIcon} />
                <span>{`Time left: ${timeLeft}`}</span>
              </div>
            )}
            {!isKycStarted && (
              <div className={classes.actionsContainer}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleVerifyNow}
                  fullWidth
                  size="large"
                  disabled={isVerifying}
                  className={classes.actionButtonPrimary}>
                  <span className={classes.actionButtonContent}>
                    {isVerifying && <CircularProgress size={20} color="inherit" />}
                    Verify now
                  </span>
                </Button>
              </div>
            )}
            <div className={classes.footer}>
              <img src={stripeIcon} alt="Stripe" className={classes.stripeIcon} />
              <span>Secure Verification Powered by Stripe</span>
            </div>
          </DialogContent>
        </MUIDialog>
      )}
      <IdentityVerificationStatusDialog
        open={statusDialogOpen}
        onClose={handleCloseStatusDialog}
        onRetry={handleVerifyNow}
        onVerified={refreshProfile}
      />
    </>
  );
};

export default IdentityVerificationDialog;
